import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { List } from 'immutable';

import { createPageParams } from './helpers';
import {
  PageParamsFactory,
  createRange,
  getPageParams,
  findIndex,
  cancellation,
} from 'pages/document/DocumentComponent/helpers';
import { getRotateByDocID, setRotateByDocID } from 'lib/pdfHelpers';
import { updateStyle } from 'lib/domHelpers';

import { BORDER_WIDTH, MAX_DOC_WIDTH } from 'pages/document/page';

const generatePageParams = (paymentId: string) => createPageParams(getRotateByDocID(paymentId));

interface UsePaymentHookParams {
  paymentId: string;
  width: number;
  startResizeWidth: number;
  scale: number;
}

const usePaymentHook = ({ paymentId, width, startResizeWidth, scale }: UsePaymentHookParams) => {
  const [pageParams, setPageParams] = useState<List<any>>(List([]));
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<boolean | string>(false);
  const [focusedPage, setFocusedPage] = useState(0);
  const [isPreviewOpen, setPreviewOpenState] = useState(true);
  const observersRef = useRef<IntersectionObserver[]>([]);
  const loadCancelableRef = useRef<{ execute: (...args: unknown[]) => void; onCancel: () => void } | null>(null);
  const renderCancelableRef = useRef<{ execute: (...args: unknown[]) => void; onCancel: () => void } | null>(null);
  const scrollViewRef = useRef<HTMLElement | undefined>(undefined);
  const scrollPositionRef = useRef(0);
  const pagesRef = useRef<HTMLElement[]>([]);
  // const { documentID, protected: docProtected } = data;
  // TODO need check protected pdf
  const docProtected = false;

  const isProtected = useMemo(() => docProtected && !password, [docProtected, password]);

  const viewportWidth = useMemo(() => Math.min(MAX_DOC_WIDTH, startResizeWidth || width), [startResizeWidth, width]);

  const frameResizeScale = useMemo(() => {
    if (startResizeWidth) {
      const maxDocWidth = MAX_DOC_WIDTH - BORDER_WIDTH;
      const currentDocWidth = width - BORDER_WIDTH;
      const startDocWidth =
        maxDocWidth > startResizeWidth - BORDER_WIDTH ? startResizeWidth - BORDER_WIDTH : maxDocWidth;
      const currentWidth = Math.min(maxDocWidth, currentDocWidth);
      return startResizeWidth >= maxDocWidth && currentDocWidth >= maxDocWidth ? 1 : currentWidth / startDocWidth;
    }
    return 1;
  }, [startResizeWidth, width]);

  const handleOnLoad = useCallback(
    ({ numPages }: { numPages: number }) => {
      const pageParamsData = generatePageParams(paymentId);
      const nextPageParams = createRange(0, numPages)
        .map(pageParamsData)
        .map((e) => (e.idx === 0 ? e.set('status', 'rendering') : e));
      setPageParams(nextPageParams);
    },
    [paymentId],
  );

  const handlePageSuccess = useCallback(
    (docID: string, { pageInfo, pageIndex }: { pageInfo: unknown; pageIndex: number }) => {
      if (docID !== paymentId) {
        console.warn('render from previous document was omitted', docID, paymentId);
        return;
      }
      const item = getPageParams(
        pageInfo,
        PageParamsFactory({
          idx: pageIndex,
          status: 'complete',
          rotate: pageParams.getIn([pageIndex, 'rotate'], 0),
          embdRotate: pageParams.getIn([pageIndex, 'embdRotate'], 0),
        }),
        viewportWidth - BORDER_WIDTH,
        scale,
      );
      updateStyle(pagesRef.current[pageIndex], [{ name: 'height', value: `${item.height}px` }]);
      setPageParams((prevPageParams) => prevPageParams.set(pageIndex, item));
    },
    [viewportWidth, paymentId, pageParams, scale],
  );

  const handleRotate = useCallback(
    (idx: number, n: number) => {
      setPageParams((prevPageParams) => prevPageParams.setIn([idx, 'rotate'], n));
      setRotateByDocID(paymentId, { [idx]: n });
    },
    [paymentId],
  );

  // define focused page from visible.
  const resolveFocusedPage = useCallback(() => {
    if (!scrollViewRef.current) return;
    const box = scrollViewRef.current.getBoundingClientRect();
    const overlapMap = pagesRef.current.map((el, idx) => {
      const { top, bottom, height } = el.getBoundingClientRect();
      const cTop = Math.max(box.top, top);
      const cBottom = Math.min(box.bottom, bottom);
      const overlapRatio = (cBottom - cTop) / height;
      return { idx, overlapRatio };
    });

    const focused = overlapMap.reduce((res, item) => (item.overlapRatio > res.overlapRatio ? item : res), {
      idx: 0,
      overlapRatio: 0,
    });
    setFocusedPage(focused.idx);
  }, []);

  const handleVisible = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const { isIntersecting, target } = entries[0];
      const finder = findIndex(target);
      const idx = pagesRef.current.reduce(finder, undefined);

      if (isIntersecting && typeof idx === 'number') {
        if (pageParams.getIn([idx, 'status']) === null) {
          setPageParams((prevPageParams) => prevPageParams.setIn([idx, 'status'], 'rendering'));
        }
      }
    },
    [pageParams],
  );

  const handlePageRef = useCallback(
    (index: number) => (element: HTMLElement | null) => {
      if (element) {
        pagesRef.current[index] = element;
        // eslint-disable-next-line no-undef
        if (typeof observersRef.current[index] === 'undefined') {
          observersRef.current[index] = new IntersectionObserver(handleVisible, {
            root: scrollViewRef.current,
            rootMargin: '500px',
            threshold: 0,
          });
          updateStyle(element, [
            {
              name: 'height',
              value: `${viewportWidth * Math.sqrt(2)}px`,
            },
          ]);
          observersRef.current[index].observe(element);
        }
      }
    },
    [handleVisible, viewportWidth],
  );

  const onScroll = useCallback(
    (e: React.UIEvent<HTMLElement>) => {
      if (Math.abs(scrollPositionRef.current - e.currentTarget.scrollTop) > 80 || e.currentTarget.scrollTop === 0) {
        scrollPositionRef.current = e.currentTarget.scrollTop;
        resolveFocusedPage();
      }
    },
    [resolveFocusedPage],
  );

  const onPasswordEnter = useCallback((passwordInput: string) => {
    setPassword(passwordInput);
    setPasswordError(false);
  }, []);

  const onDocumentLoadError = useCallback((error: Error & { name: string }) => {
    console.warn('PDF LOAD ERROR: ', error);
    if (error.name === 'PasswordException') {
      setPassword('');
      setPasswordError(true);
    }
  }, []);

  const isPageShow = useCallback((params: { status: unknown }) => Boolean(params.status), []);

  const handleViewportRef = useCallback((ref: HTMLElement | null) => {
    scrollViewRef.current = ref ?? undefined;
  }, []);

  const setPreviewOpen = useCallback((isOpenState: boolean) => {
    setPreviewOpenState(isOpenState);
  }, []);

  useEffect(
    () => () => {
      if (loadCancelableRef.current) {
        loadCancelableRef.current.onCancel();
      }
      if (renderCancelableRef.current) {
        renderCancelableRef.current.onCancel();
      }
      observersRef.current.forEach((o) => o.disconnect());
    },
    [],
  );

  const selectPage = useCallback((index: number) => {
    const pageEl = pagesRef.current[index];
    if (pageEl) {
      pageEl.scrollIntoView(true);
      setFocusedPage(index);
    }
  }, []);

  loadCancelableRef.current = cancellation(handleOnLoad);
  renderCancelableRef.current = cancellation(handlePageSuccess);

  return {
    selectPage,
    pageParams,
    password,
    passwordError,
    onPasswordEnter,
    onDocumentLoadError,
    frameResizeScale,
    isProtected,
    isPageShow,
    viewportWidth,
    handleViewportRef,
    handlePageRef,
    handleRotate,
    scrollViewRef: scrollViewRef.current,
    renderCancelable: renderCancelableRef.current,
    loadCancelable: loadCancelableRef.current,
    setPasswordError,
    pagesRef,
    onScroll,
    focusedPage,
    isPreviewOpen,
    setPreviewOpen,
  };
};

export default usePaymentHook;
